import React, { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { Helmet } from "react-helmet";
import {
  UserCheck,
  Users,
  Building,
  ChevronUp,
  ChevronDown,
  X,
  Phone,
} from "lucide-react";
import { Spinner } from "../components";
import { useAuth } from "../hooks/AuthenticationProvider";

const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
      <div className="bg-[#18181B] rounded-xl border border-slate-700/50 w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 border-b border-slate-700/50">
          <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800/50 rounded-lg"
          >
            <X className="text-slate-400 hover:text-slate-300" />
          </button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
};

const PlatformManager = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [name, setName] = useState("");
  const [showPhoneNumbers, setShowPhoneNumbers] = useState(null); // Track which org's numbers to show
  const [phoneNumbers, setPhoneNumbers] = useState([]); // Store phone numbers
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [showAddPhoneNumberModal, setShowAddPhoneNumberModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUsers, setShowUsers] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showOrgUsers, setShowOrgUsers] = useState(false);
  const [editingExpiration, setEditingExpiration] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState("");
  const [dashboardCounts, setDashboardCounts] = useState({});
  const [editingBeds, setEditingBeds] = useState(null);
  const [newBedCount, setNewBedCount] = useState("");
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [editingDashboards, setEditingDashboards] = useState(null);
  const [newDashboardCount, setNewDashboardCount] = useState("");
  const [bedCounts, setBedCounts] = useState({});
  const [email, setEmail] = useState("");
  const isPlatformManagerAllowed =
    user?.email === "adam@reverencehouse.com" ||
    user?.email === "recoveryhouseplatform@gmail.com";

  if (!isPlatformManagerAllowed) {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    fetchOrganizations();
    fetchPendingApprovals();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const orgsRef = collection(db, "organizations");
      const snapshot = await getDocs(orgsRef);
      const orgsData = [];

      for (const orgDoc of snapshot.docs) {
        try {
          const orgData = orgDoc.data();
          const usersRef = collection(db, "organizations", orgDoc.id, "users");
          const usersSnapshot = await getDocs(usersRef);
          const users = usersSnapshot.docs.map((userDoc) => ({
            id: userDoc.id,
            ...userDoc.data(),
          }));

          let houseSettings = null;
          let houseCount = 0;

          try {
            const houseSettingsRef = doc(
              db,
              "organizations",
              orgDoc.id,
              "houseSettings",
              "initial"
            );
            const houseSettingsSnap = await getDoc(houseSettingsRef);
            houseSettings = houseSettingsSnap.exists()
              ? houseSettingsSnap.data()
              : null;

            if (houseSettings?.houses && Array.isArray(houseSettings.houses)) {
              houseCount = houseSettings.houses.length;
            }
          } catch (houseError) {
            console.error("Error fetching house settings:", houseError);
          }

          orgsData.push({
            id: orgDoc.id,
            ...orgData,
            users,
            organizationName: houseSettings?.organizationName || orgDoc.id,
            totalBeds: houseSettings?.paidBedCount || 0,
            dashboardCount: houseSettings?.paidResDashboardCount || 0,
            houseCount: houseCount || 0,
            houses: houseSettings?.houses || [],
            created: houseSettings?.created?.toDate() || null,
            expirationDate: houseSettings?.expirationDate?.toDate() || null,
          });
        } catch (orgError) {
          console.error("Error processing organization:", orgDoc.id, orgError);
        }
      }

      setOrganizations(orgsData);
      setLoading(false);
    } catch (error) {
      console.error("Error in fetchOrganizations:", error);
      setLoading(false);
    }
  };

  const handleExpirationUpdate = async (orgId, newDate) => {
    try {
      const houseSettingsRef = doc(
        db,
        "organizations",
        orgId,
        "houseSettings",
        "initial"
      );
      const snapshot = await getDoc(houseSettingsRef);

      if (snapshot.exists()) {
        await updateDoc(houseSettingsRef, {
          expirationDate: new Date(newDate),
        });
      } else {
        await setDoc(houseSettingsRef, {
          expirationDate: new Date(newDate),
        });
      }

      setOrganizations((orgs) =>
        orgs.map((org) =>
          org.id === orgId ? { ...org, expirationDate: new Date(newDate) } : org
        )
      );
      setEditingExpiration(null);
      setNewExpirationDate("");
    } catch (error) {
      console.error("Error updating expiration date:", error);
      alert("Failed to update expiration date");
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const pendingUsersRef = collection(db, "pendingUsers");
      const snapshot = await getDocs(pendingUsersRef);

      const pendingOrgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPendingApprovals(pendingOrgs);
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
    }
  };

  const fetchPhoneNumbers = async (orgId) => {
    try {
      setLoadingPhoneNumbers(true);
      const phoneNumbersRef = collection(
        db,
        `organizations/${orgId}/phoneNumbers`
      );
      const snapshot = await getDocs(phoneNumbersRef);

      const numbers = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPhoneNumbers(numbers);
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const handleShowPhoneNumbers = async (orgId) => {
    if (showPhoneNumbers === orgId) {
      // If the same organization is clicked, close the list
      setShowPhoneNumbers(null);
      setPhoneNumbers([]); // Clear the phone numbers when closed
    } else {
      // Otherwise, fetch and display the phone numbers
      setShowPhoneNumbers(orgId);
      await fetchPhoneNumbers(orgId);
    }
  };

  const handleDeletePhoneNumber = async (orgId, numberId) => {
    try {
      const numberRef = doc(
        db,
        `organizations/${orgId}/phoneNumbers`,
        numberId
      );
      await deleteDoc(numberRef);

      // Refresh the phone numbers list
      await fetchPhoneNumbers(orgId);
      alert("Phone number deleted successfully.");
    } catch (error) {
      console.error("Error deleting phone number:", error);
      alert("Failed to delete phone number.");
    }
  };

  const handleAddPhoneNumber = async () => {
    if (!selectedOrg || !phoneNumber || !name || !email) {
      alert(
        "Please select an organization, enter a name, email, and a phone number."
      );
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    // Clean and format the phone number
    let formattedNumber = phoneNumber.replace(/\D/g, ""); // Removes non-digit characters

    // Handle edge cases for phone number formatting
    if (formattedNumber.startsWith("1") && formattedNumber.length === 11) {
      formattedNumber = `+${formattedNumber}`;
    } else if (formattedNumber.length === 10) {
      formattedNumber = `+1${formattedNumber}`;
    } else if (!formattedNumber.startsWith("+")) {
      alert("Invalid phone number format. Please recheck.");
      return;
    }

    try {
      const phoneNumbersRef = collection(
        db,
        `organizations/${selectedOrg.id}/phoneNumbers`
      );
      await addDoc(phoneNumbersRef, {
        name, // Save the name
        phoneNumber: formattedNumber, // Save the formatted phone number
        email: email.toLowerCase().trim(), // Save the email (normalized)
      });

      alert("Phone number added successfully!");
      setPhoneNumber("");
      setName("");
      setEmail("");
      setSelectedOrg(null);
      setShowAddPhoneNumberModal(false);
    } catch (error) {
      console.error("Error adding phone number:", error);
      alert("Failed to add phone number.");
    }
  };

  const handleBedCountChange = (email, count) => {
    setBedCounts((prev) => ({
      ...prev,
      [email]: parseInt(count) || 0,
    }));
  };

  const handleDashboardCountChange = (email, count) => {
    setDashboardCounts((prev) => ({
      ...prev,
      [email]: parseInt(count) || 0,
    }));
  };

  const handleBedUpdate = async (orgId, newCount) => {
    try {
      const houseSettingsRef = doc(
        db,
        "organizations",
        orgId,
        "houseSettings",
        "initial"
      );
      const snapshot = await getDoc(houseSettingsRef);

      if (snapshot.exists()) {
        const currentSettings = snapshot.data();
        await updateDoc(houseSettingsRef, {
          ...currentSettings,
          paidBedCount: parseInt(newCount),
        });
      } else {
        await setDoc(houseSettingsRef, {
          paidBedCount: parseInt(newCount),
        });
      }

      setOrganizations((orgs) =>
        orgs.map((org) =>
          org.id === orgId ? { ...org, totalBeds: parseInt(newCount) } : org
        )
      );
      setEditingBeds(null);
      setNewBedCount("");
    } catch (error) {
      console.error("Error updating bed count:", error);
      alert("Failed to update bed count");
    }
  };

  const handleDashboardUpdate = async (orgId, newCount) => {
    try {
      const houseSettingsRef = doc(
        db,
        "organizations",
        orgId,
        "houseSettings",
        "initial"
      );
      const snapshot = await getDoc(houseSettingsRef);

      if (snapshot.exists()) {
        const currentSettings = snapshot.data();
        await updateDoc(houseSettingsRef, {
          ...currentSettings,
          paidResDashboardCount: parseInt(newCount),
        });
      } else {
        await setDoc(houseSettingsRef, {
          paidResDashboardCount: parseInt(newCount),
        });
      }

      setOrganizations((orgs) =>
        orgs.map((org) =>
          org.id === orgId
            ? { ...org, dashboardCount: parseInt(newCount) }
            : org
        )
      );
      setEditingDashboards(null);
      setNewDashboardCount("");
    } catch (error) {
      console.error("Error updating dashboard count:", error);
      alert("Failed to update dashboard count");
    }
  };

  const handleApproval = async (email, approved) => {
    try {
      const pendingUserRef = doc(db, "pendingUsers", email);
      const pendingUserDoc = await getDoc(pendingUserRef);

      if (!pendingUserDoc.exists()) return;

      if (approved) {
        if (!bedCounts[email]) {
          alert("Please set a bed count before approving");
          return;
        }

        await updateDoc(pendingUserRef, {
          status: "Approved",
          paidBedCount: bedCounts[email],
          paidResDashboardCount: dashboardCounts[email] || 0,
        });

        setPendingApprovals((prev) =>
          prev.map((approval) =>
            approval.email === email
              ? {
                  ...approval,
                  status: "Approved",
                  paidBedCount: bedCounts[email],
                  paidResDashboardCount: dashboardCounts[email] || 0,
                }
              : approval
          )
        );

        alert(
          `${pendingUserDoc.data().firstName || "User"} has been approved!`
        );
      } else {
        await updateDoc(pendingUserRef, { status: "Denied" });
        setPendingApprovals((prev) =>
          prev.map((approval) =>
            approval.email === email
              ? { ...approval, status: "Denied" }
              : approval
          )
        );
      }
    } catch (error) {
      console.error("Error in handleApproval:", error);
      alert("An error occurred while processing the approval.");
    }
  };

  const updateUserRole = async (orgId, userId, newRole) => {
    try {
      const userRef = doc(db, "organizations", orgId, "users", userId);
      await updateDoc(userRef, { role: newRole });
      fetchOrganizations();
    } catch (error) {
      console.error("Error updating user role:", error);
    }
  };

  const toggleOrgDropdown = (orgId) => {
    setExpandedOrg((prev) => (prev === orgId ? null : orgId));
  };

  const toggleUsers = (orgId) => {
    setShowUsers((prev) => (prev === orgId ? null : orgId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Platform Manager</title>
      </Helmet>

      <div className="min-h-screen bg-zinc-900 p-3 sm:p-6">
        <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Platform Manager
            </h1>
          </div>

          {/* Pending Approvals Section */}
          <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="text-orange-400" />
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                Pending Approvals
              </h2>
            </div>

            {pendingApprovals.length === 0 ? (
              <p className="text-slate-400">No pending approvals</p>
            ) : (
              <div className="space-y-4">
                {pendingApprovals.map((approval) => (
                  <div
                    key={approval.email}
                    className="bg-slate-900/50 rounded-lg p-3 sm:p-4"
                  >
                    <div className="space-y-3">
                      <div>
                        <p className="text-white font-medium">
                          {approval.firstName} {approval.lastName}
                        </p>
                        <p className="text-sm text-slate-400">
                          {approval.email}
                        </p>
                        <p className="text-sm text-orange-400">
                          {approval.status}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-400">
                            Paid Bed Count:
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={bedCounts[approval.email] || ""}
                            onChange={(e) =>
                              handleBedCountChange(
                                approval.email,
                                e.target.value
                              )
                            }
                            className="bg-slate-700 text-white rounded px-3 py-1 w-16 sm:w-24"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-slate-400">
                            Resident Dashboard Count:
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={dashboardCounts[approval.email] || ""}
                            onChange={(e) =>
                              handleDashboardCountChange(
                                approval.email,
                                e.target.value
                              )
                            }
                            className="bg-slate-700 text-white rounded px-3 py-1 w-16 sm:w-24"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end mt-3">
                        <button
                          onClick={() => handleApproval(approval.email, true)}
                          className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                          disabled={!bedCounts[approval.email]}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleApproval(approval.email, false)}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Organizations List */}
          <div className="bg-slate-800/50 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="text-orange-400" />
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                Organizations
              </h2>
            </div>

            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowAddPhoneNumberModal(true)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 text-sm sm:text-base"
              >
                Add Phone Number
              </button>
            </div>

            <div className="space-y-4">
              {organizations.map((org) => (
                <div key={org.id} className="bg-slate-800/50 rounded-lg p-4">
                  {/* Organization Header */}
                  <div className="flex justify-between items-start sm:items-center">
                    <div className="space-y-2 w-5/6 sm:w-auto">
                      <h2 className="text-lg sm:text-xl font-bold text-green-400 break-words">
                        {org.organizationName ||
                          org.name ||
                          "Unnamed Organization"}
                      </h2>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        {/* Bed Count */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm text-slate-400">
                            Beds:
                          </span>
                          {editingBeds === org.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                value={newBedCount}
                                onChange={(e) => setNewBedCount(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-slate-700 text-white rounded px-2 py-1 w-14 sm:w-20 text-xs sm:text-sm"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBedUpdate(org.id, newBedCount);
                                }}
                                className="text-xs sm:text-sm bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            <span
                              className="text-xs sm:text-sm text-white hover:text-orange-400 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingBeds(org.id);
                                setNewBedCount(org.totalBeds.toString());
                              }}
                            >
                              {org.totalBeds}
                            </span>
                          )}
                        </div>

                        {/* Dashboard Count */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm text-slate-400">
                            Dashboards:
                          </span>
                          {editingDashboards === org.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                value={newDashboardCount}
                                onChange={(e) =>
                                  setNewDashboardCount(e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="bg-slate-700 text-white rounded px-2 py-1 w-14 sm:w-20 text-xs sm:text-sm"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDashboardUpdate(
                                    org.id,
                                    newDashboardCount
                                  );
                                }}
                                className="text-xs sm:text-sm bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            <span
                              className="text-xs sm:text-sm text-white hover:text-orange-400 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDashboards(org.id);
                                setNewDashboardCount(
                                  org.dashboardCount?.toString() || "0"
                                );
                              }}
                            >
                              {org.dashboardCount || 0}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="text-slate-400">Created:</span>
                          <span className="text-white">
                            {org.created
                              ? new Date(org.created).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2">
                          <span className="text-slate-400">Expiration:</span>
                          {editingExpiration === org.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="date"
                                value={newExpirationDate}
                                onChange={(e) =>
                                  setNewExpirationDate(e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="bg-slate-700 text-white rounded px-2 py-1 text-xs sm:text-sm w-32"
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpirationUpdate(
                                    org.id,
                                    newExpirationDate
                                  );
                                }}
                                className="text-xs sm:text-sm bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700"
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            <span
                              className="text-white hover:text-orange-400 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingExpiration(org.id);
                                setNewExpirationDate(
                                  org.expirationDate
                                    ? org.expirationDate
                                        .toISOString()
                                        .split("T")[0]
                                    : ""
                                );
                              }}
                            >
                              {org.expirationDate
                                ? new Date(
                                    org.expirationDate
                                  ).toLocaleDateString()
                                : "Not set"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleOrgDropdown(org.id)}
                    >
                      {expandedOrg === org.id ? (
                        <ChevronUp className="text-slate-400" />
                      ) : (
                        <ChevronDown className="text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Organization Details */}
                  {expandedOrg === org.id && (
                    <div className="mt-4 space-y-4">
                      {/* Houses Section */}
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                          Houses
                        </h3>
                        <div className="space-y-2">
                          {org.houses.length === 0 ? (
                            <p className="text-slate-400 text-sm">
                              No houses configured
                            </p>
                          ) : (
                            org.houses.map((house, index) => (
                              <div
                                key={index}
                                className="flex justify-between items-center bg-slate-900 rounded-lg p-3 sm:p-4"
                                style={{
                                  borderLeft: `4px solid ${
                                    house.color || "#666"
                                  }`,
                                }}
                              >
                                <div>
                                  <h3 className="text-base sm:text-lg font-medium text-white break-words">
                                    {house.name}
                                  </h3>
                                  <p className="text-xs sm:text-sm text-slate-400">
                                    Fee: ${house.fee}/month
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                        {/* Users Button */}
                        <button
                          className="flex items-center justify-center gap-2 bg-blue-600 bg-opacity-80 text-white px-3 py-2 rounded-lg hover:bg-opacity-100 text-sm sm:text-base"
                          onClick={() => toggleUsers(org.id)}
                        >
                          <Users size={16} />
                          {org.users.length} Users
                        </button>

                        {/* Phone Numbers Button */}
                        <button
                          className="flex items-center justify-center gap-2 bg-green-600 bg-opacity-80 text-white px-3 py-2 rounded-lg hover:bg-opacity-100 text-sm sm:text-base"
                          onClick={() => handleShowPhoneNumbers(org.id)}
                        >
                          <Phone size={16} />
                          <span className="sm:block">View Phone Numbers</span>
                        </button>
                      </div>

                      {/* Phone Numbers Panel */}
                      {showPhoneNumbers === org.id && (
                        <div className="mt-4 space-y-2 bg-slate-800/50 p-3 sm:p-4 rounded-lg">
                          <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                            Phone Numbers
                          </h3>

                          {loadingPhoneNumbers ? (
                            <p className="text-slate-400">
                              Loading phone numbers...
                            </p>
                          ) : phoneNumbers.length === 0 ? (
                            <p className="text-slate-400">
                              No phone numbers available.
                            </p>
                          ) : (
                            phoneNumbers.map((number) => (
                              <div
                                key={number.id}
                                className="flex justify-between items-center bg-slate-900 rounded-lg p-3 sm:p-4"
                              >
                                <div className="w-5/6">
                                  <p className="text-white font-medium break-words">
                                    {number.name}
                                  </p>
                                  <p className="text-sm text-slate-400 break-words">
                                    {number.phoneNumber}
                                  </p>
                                  {number.email && (
                                    <p className="text-xs text-slate-400 break-words">
                                      {number.email}
                                    </p>
                                  )}
                                </div>
                                <button
                                  className="text-red-500 hover:text-red-700 p-2"
                                  onClick={() =>
                                    handleDeletePhoneNumber(org.id, number.id)
                                  }
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}

                      {/* Users List */}
                      {showUsers === org.id && (
                        <div className="mt-4 space-y-2">
                          {org.users.length === 0 ? (
                            <p className="text-slate-400 text-sm">
                              No users found
                            </p>
                          ) : (
                            org.users.map((user) => (
                              <div
                                key={user.id}
                                className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-900 rounded-lg p-3 sm:p-4 gap-3"
                              >
                                <div className="space-y-1">
                                  <p className="text-white font-medium break-words">
                                    {user.firstName} {user.lastName}
                                  </p>
                                  <p className="text-sm text-slate-400 break-words">
                                    {user.email}
                                  </p>
                                  <p className="text-sm text-orange-400">
                                    Role: {user.role}
                                  </p>
                                </div>
                                <select
                                  value={user.role || ""}
                                  onChange={(e) =>
                                    updateUserRole(
                                      org.id,
                                      user.id,
                                      e.target.value
                                    )
                                  }
                                  className="bg-slate-700 text-white rounded-lg px-2 py-1 text-sm"
                                >
                                  <option value="Owner">Owner</option>
                                  <option value="Admin">Admin</option>
                                  <option value="Staff">Staff</option>
                                  <option value="Leader">Leader</option>
                                </select>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddPhoneNumberModal}
        onClose={() => {
          setShowAddPhoneNumberModal(false);
          setPhoneNumber("");
          setName("");
          setEmail("");
          setSelectedOrg(null);
        }}
        title="Add Phone Number"
      >
        <div className="space-y-4">
          {/* Organization Dropdown */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              Select Organization
            </label>
            <select
              value={selectedOrg?.id || ""}
              onChange={(e) =>
                setSelectedOrg(
                  organizations.find((org) => org.id === e.target.value)
                )
              }
              className="w-full bg-slate-700 text-white rounded-lg px-3 py-2"
            >
              <option value="" disabled>
                Select an organization
              </option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.organizationName || "Unknown Name"}
                </option>
              ))}
            </select>
          </div>

          {/* Name Input */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name (e.g., John Doe)"
              className="w-full bg-slate-700 text-white rounded-lg px-3 py-2"
            />
          </div>

          {/* Email Input */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email (e.g., john@example.com)"
              className="w-full bg-slate-700 text-white rounded-lg px-3 py-2"
            />
          </div>

          {/* Phone Number Input */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">
              Phone Number
            </label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number (e.g., 5551234567)"
              className="w-full bg-slate-700 text-white rounded-lg px-3 py-2"
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 mt-4">
            <button
              onClick={() => setShowAddPhoneNumberModal(false)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 sm:order-1 order-2"
            >
              Cancel
            </button>
            <button
              onClick={handleAddPhoneNumber}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 sm:order-2 order-1"
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>

      {/* Organization Users Modal */}
      <Modal
        isOpen={showOrgUsers}
        onClose={() => {
          setShowOrgUsers(false);
          setSelectedOrg(null);
        }}
        title={selectedOrg?.name}
      >
        <div className="space-y-4">
          {selectedOrg?.users.map((user) => (
            <div
              key={user.id}
              className="bg-slate-800/50 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3"
            >
              <div>
                <p className="text-white font-medium">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-slate-400">{user.email}</p>
                <p className="text-sm text-orange-400">{user.role}</p>
              </div>
              <select
                value={user.role || ""}
                onChange={(e) =>
                  updateUserRole(selectedOrg.id, user.id, e.target.value)
                }
                className="bg-slate-700 text-white rounded-lg px-3 py-2 w-full sm:w-auto"
              >
                <option value="Owner">Owner</option>
                <option value="Admin">Admin</option>
                <option value="Staff">Staff</option>
                <option value="Leader">Leader</option>
              </select>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default PlatformManager;
